export type MicrophoneFailureCode =
  | "not-allowed"
  | "not-found"
  | "not-readable"
  | "unsupported"
  | "audio-context-failed"
  | "recorder-failed"
  | "get-user-media-failed";

export type MicrophoneDiagnostic = {
  code: MicrophoneFailureCode;
  userMessage: string;
  debugMessage: string;
};

export class MicrophoneAccessError extends Error {
  readonly code: MicrophoneFailureCode;

  constructor(code: MicrophoneFailureCode, message: string) {
    super(message);
    this.name = "MicrophoneAccessError";
    this.code = code;
  }
}

export function diagnoseMicrophoneError(caught: unknown, operation: string): MicrophoneDiagnostic {
  const name = caught instanceof Error ? caught.name : "UnknownError";
  const detail = caught instanceof Error ? caught.message : String(caught);
  let code: MicrophoneFailureCode = caught instanceof MicrophoneAccessError ? caught.code : "get-user-media-failed";
  if (!(caught instanceof MicrophoneAccessError)) {
    if (name === "NotAllowedError" || name === "SecurityError") code = "not-allowed";
    else if (name === "NotFoundError" || name === "DevicesNotFoundError") code = "not-found";
    else if (name === "NotReadableError" || name === "TrackStartError" || name === "AbortError") code = "not-readable";
    else if (name === "TypeError" && !globalThis.navigator?.mediaDevices?.getUserMedia) code = "unsupported";
  }
  const userMessage: Record<MicrophoneFailureCode, string> = {
    "not-allowed": "マイクを開始できません。アプリのマイク許可を確認して、もう一度試してください。",
    "not-found": "使用できるマイクが見つかりません。入力機器の接続を確認してください。",
    "not-readable": "マイクを開けません。他の録音アプリを閉じて、もう一度試してください。",
    "unsupported": "この環境はマイク入力に対応していません。",
    "audio-context-failed": "音声処理を開始できません。アプリを開き直して再試行してください。",
    "recorder-failed": "録音機能を開始できません。端末の音声機能を確認してください。",
    "get-user-media-failed": "マイク入力を開始できませんでした。入力機器を確認してください。",
  };
  return {
    code,
    userMessage: userMessage[code],
    debugMessage: [
      `operation=${operation}`,
      `code=${code}`,
      `name=${name}`,
      `detail=${detail || "none"}`,
      `secureContext=${globalThis.isSecureContext ?? "unknown"}`,
      `mediaDevices=${Boolean(globalThis.navigator?.mediaDevices)}`,
    ].join(" "),
  };
}
